import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AbstractPaymentMethod,
  AbstractPaymentPlatformService,
  AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE,
  isAWalletPaymentMethodType,
  PAYMENT_METHODS_WITH_BILLING_ADDRESS_REQUIRED,
  PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED,
  shouldCollectEmailInElement,
  PaymentMethodDeletionBlockedError,
  PaymentMethodMode,
  PaymentPlatformPaymentData,
  StripePaymentMethod,
} from '../../models';
import { BehaviorSubject, combineLatest, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pairwise, skip, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  Address as StripeAddress,
  loadStripe,
  PaymentMethod,
  SetupIntent,
  Stripe,
  StripeAddressElement,
  StripeAddressElementChangeEvent,
  StripeElements,
  StripeElementsOptionsClientSecret,
  StripeError,
  StripePaymentElement,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import {
  Address,
  AiwynPaymentMethod,
  BillingDetails,
  CancelPaymentPayload,
  ChargePayload,
  PaymentActionRequirement,
  PaymentMethodApiService,
  PaymentMethodType,
  PaymentPlatform,
  PaymentPlatformBillingDetails,
  PaymentResult,
  PaymentsApiService,
} from '@aiwyn/api';
import {
  areStringsEqualIgnoreCase,
  CountryAlpha2Code,
  DEFAULT_LOADABLE_STATE,
  getLoadedResults,
  ignoreNullish,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  logEmissions,
  Logger,
  mapToLoadable,
} from '@aiwyn/shared';
import { Store } from '@ngxs/store';
import { ConfigService } from '@aiwyn/config';
import {
  DeletePaymentMethod,
  GetSavedPaymentMethods,
  GetSetupIntentClientSecret,
  GetSetupIntentClientSecretByBusinessEntity,
  GetStripeUserDetails,
  RemoveNewBillingDetails,
  RemoveTemporaryPaymentMethod,
  SavePaymentMethod,
  SetDefaultPaymentMethod,
  SetNewBillingDetails,
  SetNewPaymentMethodType,
  SetPaymentMethodMode,
  SetSelectedBusinessEntityId,
  SetSelectedSavedPaymentMethodId,
  SetSuccessfulSetupIntentId,
  SetTemporaryPaymentMethod,
  StripeState,
  UpdatePaymentMethodBillingDetails,
} from '../state';
import { getWhiteLabeledStripeElementAppearance, StripElementsInitializationData } from '../models';
import { AuthStateAdaptorService } from '@aiwyn/auth';

interface StripeUserDetails {
  userId: string;
  defaultPaymentMethodId: string;
}

// Matches DetachPaymentMethodWithPendingPaymentsError in anduin (PaymentHumanReadableException.kt).
const PENDING_PAYMENT_ERROR_FRAGMENT = 'payment that is still being processed';

function isPendingPaymentsBackendError(error: unknown): boolean {
  return error instanceof HttpErrorResponse
    && error.status === 400
    && typeof error.error?.message === 'string'
    && error.error.message.includes(PENDING_PAYMENT_ERROR_FRAGMENT);
}

@Injectable({
  providedIn: 'root',
})
export class StripePlatformService implements AbstractPaymentPlatformService, OnDestroy {
  instance$ = new BehaviorSubject<Loadable<Stripe>>(DEFAULT_LOADABLE_STATE);

  elements$ = new BehaviorSubject<StripeElements>(undefined);
  paymentElement$ = new BehaviorSubject<StripePaymentElement>(undefined);
  addressElement$ = new BehaviorSubject<StripeAddressElement>(undefined);

  onPaymentElementChange$ = new BehaviorSubject<StripePaymentElementChangeEvent>(undefined);
  onAddressElementChange$ = new BehaviorSubject<StripeAddressElementChangeEvent>(undefined);

  isAddPaymentMethodFlowActive$ = new BehaviorSubject(false);

  constructor(
    private readonly configService: ConfigService,
    private readonly store: Store,
    private readonly paymentsApiService: PaymentsApiService,
    private readonly paymentMethodApiService: PaymentMethodApiService,
    private readonly logger: Logger,
    private readonly authStateAdaptor: AuthStateAdaptorService,
  ) {
    combineLatest([
      getLoadedResults(this.instance$),
      getLoadedResults(this.store.select(StripeState.getSetupIntentClientSecretLoadable)).pipe(distinctUntilChanged()),
      // Refreshing `elements$` ensures the payment element doesn't retain the last selected payment method type after being destroyed.
      this.isAddPaymentMethodFlowActive$.pipe(
        startWith(true), // this alongside the subject's initial value of `false` ensures the `combineLatest` isn't held up
        pairwise(),
        filter(([previous, current]) => previous && !current),
      ),
    ]).pipe(
      tap(([stripeInstance, setupIntentClientSecret]) => {
        this.destroyPaymentElement();
        this.destroyAddressElement();

        const baseElementsOptions = this._getBaseElementsOptions(setupIntentClientSecret);
        this.elements$.next(stripeInstance.elements(baseElementsOptions));
      }),
      takeUntil(this._unsubscribe$),
    ).subscribe();

    this._activeSetupIntent$.subscribe();
  }

  private readonly _unsubscribe$ = new Subject<void>();
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  //#region metadata
  get platform(): Observable<PaymentPlatform> {
    return of(PaymentPlatform.STRIPE);
  }

  initialize(): Observable<unknown> {
    this.logger.info('PAYMENT : StripePlatformService : initialize');
    return combineLatest([
      this._initializeStripe(),
      this._getStripeUserDetails(),
      this.getSetupIntentClientSecret().pipe(catchError(_ => of(undefined))), // don't block initialization if this fails
    ]).pipe(
      take(1),
      logEmissions(this.logger, 'PAYMENT : StripePlatformService : initialize'),
    );
  }

  reset(): Observable<undefined> {
    this.destroyPaymentElement();
    this.destroyAddressElement();
    this.instance$.next(DEFAULT_LOADABLE_STATE);
    this.elements$.next(undefined);

    return of(undefined);
  }

  getPlatformInitErrors(): Observable<{ message: string }> {
    return this.instance$.pipe(
      filter(loadable => loadable.status === LoadableStatus.ERROR),
      map(loadable => ({ message: loadable.error?.message ?? 'Stripe failed to initialize' })),
    );
  }
  //#endregion

  //#region stripe specific
  private _initializeStripe(): Observable<Loadable<Stripe>> {
    if (!LoadableHelper.shouldRequest(this.instance$.value.status)) {
      return of(this.instance$.value);
    }

    this.logger.debug('PAYMENT : StripePlatformService : _initializeStripe : Stripe is not yet initialized. Can continue.');

    const paymentPublicApiKey = this.configService.paymentPublicApiKey
      ?? this._getDefaultStripePublicKey(this.configService.stripeProductionModeEnabled);

    return from(loadStripe(paymentPublicApiKey)).pipe(
      take(1),
      mapToLoadable(undefined as Stripe, true),
      logEmissions(this.logger, 'PAYMENT : StripePlatformService : _initializeStripe'),
      tap(stripeLoadable => this.instance$.next(stripeLoadable)),
      skip(1), // skip initial LOADING value
    );
  }

  private _getDefaultStripePublicKey(stripeProdModeEnabled: boolean): string {
    return stripeProdModeEnabled
      ? 'pk_live_51Ho8XIIog7gyKVjpFlk6zwb9wAldaIsUbbLxb17J3g4qCEDKle3qXofC3nAinfBVuVWXbYiN480iPyetmL0t78TW000gramSbh'
      : 'pk_test_51Ho8XIIog7gyKVjp8wl74oULqwhJjWF7GPul4v7Ssst0EF2AuYNUpRkEeR1nmeFzbvgI9v3E8MPDguXztxKS3THu00LMnQaz7H';
  }

  private _getStripeUserDetails(forceRefresh = false): Observable<StripeUserDetails> {
    const stripeUserDetailsStatus = LoadableHelper.coalesceStatus([
      this.store.selectSnapshot(StripeState.getUserIdStatus),
      this.store.selectSnapshot(StripeState.getDefaultPaymentMethodIdStatus),
    ]);

    if (!(LoadableHelper.shouldRequest(stripeUserDetailsStatus) || forceRefresh)) {
      return of(undefined);
    }

    this.logger.debug('PAYMENT : StripePlatformService : _getStripeUserDetails : Retrieving new Stripe user details.');
    return this.store.dispatch(new GetStripeUserDetails()).pipe(
      switchMap(_ => combineLatest([
        getLoadedResults(this.store.select(StripeState.getUserIdLoadable), true),
        getLoadedResults(this.store.select(StripeState.getDefaultPaymentMethodIdLoadable), true),
      ])),
      map(([userId, defaultPaymentMethodId]) => ({ userId, defaultPaymentMethodId })),
      logEmissions(this.logger, 'PAYMENT : StripePlatformService : _getStripeUserDetails'),
      take(1),
    );
  }

  getSetupIntentClientSecretStatus(): Observable<LoadableStatus> {
    return this.store.select(StripeState.getSetupIntentClientSecretStatus);
  }

  getSetupIntentClientSecret(forceRefresh = false): Observable<string> {
    const setupIntentClientSecretStatus = this.store.selectSnapshot(StripeState.getSetupIntentClientSecretStatus);
    if (!(LoadableHelper.shouldRequest(setupIntentClientSecretStatus) || forceRefresh)) {
      return getLoadedResults(this.store.select(StripeState.getSetupIntentClientSecretLoadable)).pipe(
        logEmissions(this.logger, 'PAYMENT : StripePlatformService : getSetupIntentClientSecret'),
      );
    }

    this.logger.info('PAYMENT : StripePlatformService : getSetupIntentClientSecret : Retrieving new setup intent client secret.');
    const action = this.configService.setupIntentByBusinessEntity
      ? new GetSetupIntentClientSecretByBusinessEntity(forceRefresh)
      : new GetSetupIntentClientSecret(forceRefresh);
    return this.store.dispatch(action).pipe(
      logEmissions(this.logger, 'PAYMENT : StripePlatformService : getSetupIntentClientSecret'),
      switchMap(_ => getLoadedResults(this.store.select(StripeState.getSetupIntentClientSecretLoadable))),
    );
  }

  // This observable originally was created each time `getActiveSetupIntent()` was called, which caused redundant calls to the Stripe API.
  private readonly _activeSetupIntentSubject$ = new BehaviorSubject<SetupIntent>(undefined);
  private readonly _activeSetupIntent$ = combineLatest([
    getLoadedResults(this.instance$),
    this.store.select(StripeState.getSetupIntentClientSecret).pipe(
      distinctUntilChanged(),
    ),
  ]).pipe(
    switchMap(([stripeInstance, setupIntentClientSecret]) => {
      if (!setupIntentClientSecret) {
        return of(undefined);
      }

      return from(stripeInstance.retrieveSetupIntent(setupIntentClientSecret)).pipe(
        map(({ setupIntent }) => setupIntent),
      );
    }),
    tap(activeSetupIntent => this._activeSetupIntentSubject$.next(activeSetupIntent)),
    takeUntil(this._unsubscribe$),
  );

  getActiveSetupIntent(): Observable<SetupIntent> {
    return this._activeSetupIntentSubject$.pipe(
      ignoreNullish(),
    );
  }

  private _getBaseElementsOptions(clientSecret: string): StripeElementsOptionsClientSecret {
    return {
      clientSecret,
      appearance: getWhiteLabeledStripeElementAppearance(),
      loader: 'always',
    };
  }

  getStripeElementInitializationData(elementType: 'payment' | 'address'): Observable<StripElementsInitializationData> {
    return this.elements$.pipe(
      ignoreNullish(),
      // ensure fresh stripe elements instance is ready for use
      tap(stripeElements => {
        // have to do this because stripeElements.getElement(...) uses pseudo function overloading
        const staleElement = elementType === 'payment' ? stripeElements.getElement('payment') : stripeElements.getElement('address');
        if (staleElement != undefined) {
          // A previous cycle left its element registered on this group (teardown failure or dialog closed mid-initialization).
          // Filtering forever here rendered the dialog blank with no error (PP-3574, PP-3522, PP-3625) — destroy the stale
          // element and let initialization proceed instead.
          this.logger.warn(
            `PAYMENT : StripePlatformService : getStripeElementInitializationData : Destroying stale ${elementType} element.`,
          );
          elementType === 'payment' ? this.destroyPaymentElement() : this.destroyAddressElement();
        }
      }),
      switchMap(_ => combineLatest([
        this.getSetupIntentClientSecret(),
        this.getNewPaymentMethodType(),
        this.store.selectOnce(StripeState.getRawNewBillingDetails),
      ])),
      map(([setupIntentClientSecret, newPaymentMethodType, newBillingDetails]) => ({
        setupIntentClientSecret,
        newPaymentMethodType,
        newBillingDetails,
      })),
    );
  }

  destroyPaymentElement(): void {
    // Fall back to the element registered on the group — a dialog closed mid-initialization registers the element
    // before `paymentElement$` is populated, and it must still be torn down (PP-3574).
    const paymentElement = this.paymentElement$.value ?? this.elements$.value?.getElement('payment');
    if (paymentElement == undefined) {
      return;
    }

    this._clearAndDestroyElement(paymentElement, 'payment');
    this.paymentElement$.next(undefined);
    this.onPaymentElementChange$.next(undefined);
  }

  destroyAddressElement(): void {
    const addressElement = this.addressElement$.value ?? this.elements$.value?.getElement('address');
    if (addressElement == undefined) {
      return;
    }

    this._clearAndDestroyElement(addressElement, 'address');
    this.addressElement$.next(undefined);
    this.onAddressElementChange$.next(undefined);
  }

  /**
   * Stripe throws (e.g. "Element already destroyed") when clearing/destroying an element that is already gone. A throw here
   * previously skipped the state reset in the destroy methods above AND permanently errored the constructor's `elements$`
   * refresh subscription, bricking every subsequent add-payment-method attempt until a full page reload (PP-3574, PP-3629).
   */
  private _clearAndDestroyElement(element: StripePaymentElement | StripeAddressElement, elementType: 'payment' | 'address'): void {
    try {
      element.clear();
      element.destroy();
    } catch (error) {
      this.logger.warn(`PAYMENT : StripePlatformService : _clearAndDestroyElement : Failed to destroy ${elementType} element.`, error);
    }
  }
  //#endregion

  //#region payment methods
  getNewPaymentMethodTypeOptions(): Observable<PaymentMethodType[]> {
    return this.getActiveSetupIntent().pipe(
      map(setupIntent => setupIntent.payment_method_types as PaymentMethodType[]),
    );
  }

  getSavedPaymentMethodsStatus(): Observable<LoadableStatus> {
    return this.store.select(StripeState.getSavedPaymentMethodsStatus);
  }

  getSavedPaymentMethods(forceRefresh = false): Observable<StripePaymentMethod[]> {
    if (!(LoadableHelper.shouldRequest(this.store.selectSnapshot(StripeState.getSavedPaymentMethodsStatus)) || forceRefresh)) {
      return getLoadedResults(this.store.select(StripeState.getSavedPaymentMethodsLoadable));
    }

    return this.store.dispatch(new GetSavedPaymentMethods()).pipe(
      switchMap(_ => getLoadedResults(this.store.select(StripeState.getSavedPaymentMethodsLoadable))),
    );
  }

  getSavedPaymentMethodsExist(): Observable<boolean> {
    return this.getSavedPaymentMethods().pipe(
      map(savedPaymentMethods => savedPaymentMethods.length > 0),
    );
  }

  getSavedPaymentMethod(paymentMethodId: string): Observable<StripePaymentMethod> {
    if (paymentMethodId == undefined) {
      return of(undefined);
    }

    return this.getSavedPaymentMethods().pipe(
      map(savedPaymentMethods => savedPaymentMethods.find(({ id }) => id === paymentMethodId)),
    );
  }

  // ---

  getDefaultPaymentMethodIdStatus(): Observable<LoadableStatus> {
    return this.store.select(StripeState.getDefaultPaymentMethodIdStatus);
  }

  getDefaultPaymentMethodId(): Observable<string> {
    return getLoadedResults(this.store.select(StripeState.getDefaultPaymentMethodIdLoadable));
  }

  // ---
  createPaymentMethod(): Observable<StripePaymentMethod> {
    return this.getNewPaymentMethodType().pipe(
      tap(newPaymentMethodType => {
        if (!PAYMENT_METHODS_WITH_BILLING_ADDRESS_REQUIRED.includes(newPaymentMethodType)) {
          this.destroyAddressElement();
        }
      }),
      // Validation that, if failed, halts payment method creation. Otherwise, prep necessary details.
      switchMap(newPaymentMethodType => from(this.elements$.value.submit()).pipe(
        switchMap(({ error }) => {
          if (error != undefined) {
            return throwError(error);
          }

          const newBillingDetails$ = (
            PAYMENT_METHODS_WITH_BILLING_ADDRESS_REQUIRED.includes(newPaymentMethodType)
              ? this.getNewBillingDetails()
              : of(this._getBankBillingDetails(newPaymentMethodType))
          ).pipe(
            map(billingDetails => this._withConfirmationEmail(billingDetails, newPaymentMethodType)),
            map(this.unmarshalBillingDetails),
          );

          return combineLatest([getLoadedResults(this.instance$), newBillingDetails$]);
        }),
      )),
      take(1),
      switchMap(([stripeInstance, newBillingDetails]) => stripeInstance.confirmSetup({
        elements: this.elements$.value,
        confirmParams: {
          return_url: undefined,
          expand: ['payment_method'],
          // TODO: This overrides stripe from pulling directly from the address element, which is okay.
          //  But during the 1/3 second debounce before storing address changes, the user may submit the payment.
          //  It's unlikely, but technically possible. We'd want to allow the latest value of the address element to override what's stored.
          //  The address element needs to exist and only non-nullish values can override.
          payment_method_data: {
            billing_details: newBillingDetails,
          },
        },
        redirect: 'if_required',
      })),
      switchMap(({ setupIntent, error }) => {
        if (error) {
          return throwError(error);
        }

        const newPaymentMethod = (setupIntent?.payment_method ?? {}) as PaymentMethod;
        const createdPaymentMethod = StripePaymentMethod.marshal(newPaymentMethod);

        return this.getDefaultPaymentMethodId().pipe(
          map(defaultPaymentMethodId => StripePaymentMethod.create(createdPaymentMethod, defaultPaymentMethodId)),
        );
      }),
      take(1),
    );
  }

  onValidPaymentMethodCreation(createdPaymentMethod: AbstractPaymentMethod): Observable<unknown> {
    return this.getActiveSetupIntent().pipe(
      map(({ id }) => id),
      switchMap(setupIntentId => this.store.dispatch(new SetSuccessfulSetupIntentId(createdPaymentMethod.id, setupIntentId))),
    );
  }

  onInvalidPaymentMethodCreation(error: Error): Observable<unknown> {
    return error.message === AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE ? this.getSetupIntentClientSecret(true) : of(undefined);
  }

  savePaymentMethod(paymentMethodId: string): Observable<string> {
    return this.getActiveSetupIntent().pipe(
      switchMap(({ id }) => this.store.dispatch(new SavePaymentMethod(paymentMethodId, id))),
      map(_ => paymentMethodId),
    );
  }

  // ---

  setTemporaryPaymentMethod(paymentMethod: AiwynPaymentMethod): Observable<void> {
    return this.store.dispatch(new SetTemporaryPaymentMethod(paymentMethod));
  }

  removeTemporaryPaymentMethod(): Observable<void> {
    return this.store.dispatch(new RemoveTemporaryPaymentMethod());
  }

  getTemporaryPaymentMethod(): Observable<StripePaymentMethod> {
    return this.store.select(StripeState.getTemporaryPaymentMethod);
  }

  // ---

  setSelectedSavedPaymentMethodId(paymentMethodId: string): Observable<void> {
    // TODO: This is a temporary safeguard for when a temporary payment method is created. This gets called when it shouldn't.
    return this.getSavedPaymentMethod(paymentMethodId).pipe(
      take(1),
      ignoreNullish(),
      switchMap(_ => this.store.dispatch(new SetSelectedSavedPaymentMethodId(paymentMethodId))),
    );
  }

  getSelectedPaymentMethodId(): Observable<string> {
    return this.store.select(StripeState.getSelectedPaymentMethodId);
  }

  getSelectedSavedPaymentMethodId(): Observable<string> {
    return this.store.select(StripeState.getSelectedSavedPaymentMethodId);
  }

  // ---

  setDefaultPaymentMethod(paymentMethodId: string): Observable<void> {
    return this.store.dispatch(new SetDefaultPaymentMethod(paymentMethodId));
  }

  deletePaymentMethod(paymentMethodId: string): Observable<void> {
    const stripeUserId = this.store.selectSnapshot(StripeState.getUserId);
    return this.paymentMethodApiService.hasPendingPayments(stripeUserId, paymentMethodId).pipe(
      catchError(() => of(true)),
      switchMap(hasPendingPayments => {
        if (hasPendingPayments) {
          return throwError(() => new PaymentMethodDeletionBlockedError(false, false, true));
        }
        return this.store.dispatch(new DeletePaymentMethod(paymentMethodId)).pipe(
          // Translate the backend's pending-payment guard into the shared blocked-deletion error so the
          // existing CannotDeletePaymentMethodComponent surfaces it when the frontend guard races the backend.
          catchError(error => isPendingPaymentsBackendError(error)
            ? throwError(() => new PaymentMethodDeletionBlockedError(false, false, true))
            : throwError(() => error),
          ),
        );
      }),
    );
  }

  updatePaymentMethod(paymentMethodId: string, billingDetails: BillingDetails): Observable<string> {
    return this.store.dispatch(new UpdatePaymentMethodBillingDetails(paymentMethodId, billingDetails)).pipe(
      map(_ => paymentMethodId),
    );
  }
  //#endregion

  //#region general payment method details
  setNewPaymentMethodType(paymentMethodType: PaymentMethodType): Observable<void> {
    const existingPaymentMethodType = this.store.selectSnapshot(StripeState.getNewPaymentMethodType);
    if (paymentMethodType === existingPaymentMethodType) {
      return of(undefined);
    }

    return this.store.dispatch(new SetNewPaymentMethodType(paymentMethodType));
  }

  getNewPaymentMethodType(): Observable<PaymentMethodType> {
    return this.store.select(StripeState.getNewPaymentMethodType);
  }

  // ---

  setPaymentMethodMode(paymentMethodMode: PaymentMethodMode): Observable<void> {
    const existingPaymentMethodMode = this.store.selectSnapshot(StripeState.getPaymentMethodMode);
    if (paymentMethodMode === existingPaymentMethodMode) {
      return of(undefined);
    }

    return this.store.dispatch(new SetPaymentMethodMode(paymentMethodMode));
  }

  getPaymentMethodMode(): Observable<PaymentMethodMode> {
    return this.store.select(StripeState.getPaymentMethodMode);
  }

  // ---

  setNewBillingDetails(billingDetails: BillingDetails): Observable<void> {
    const existingBillingDetails = this.store.selectSnapshot(StripeState.getRawNewBillingDetails);
    if (billingDetails === existingBillingDetails) {
      return of(undefined);
    }

    return this.store.dispatch(new SetNewBillingDetails(billingDetails));
  }

  removeNewBillingDetails(): Observable<void> {
    const existingBillingDetails = this.store.selectSnapshot(StripeState.getRawNewBillingDetails);
    if (existingBillingDetails == undefined) {
      return of(undefined);
    }

    return this.store.dispatch(new RemoveNewBillingDetails());
  }

  getNewBillingDetails(): Observable<BillingDetails> {
    return combineLatest([
      this.store.selectOnce(StripeState.getRawNewBillingDetails),
      this.authStateAdaptor.getUserEmail(),
      this.store.selectOnce(StripeState.getProvidedBillingEmail),
    ]).pipe(
      // Prefer a buyer-supplied email (e.g. sidebar), then the account email; leave it undefined so the
      // element-collected value is used and an empty string never overwrites it (BACS mandate needs it).
      map(([billingDetails, authEmail, providedEmail]) => ({ ...billingDetails, email: providedEmail || authEmail || undefined })),
    );
  }

  getNewPaymentMethodFormComplete(): Observable<boolean> {
    return combineLatest([
      this.onPaymentElementChange$.pipe(
        ignoreNullish(),
        map(({ complete }) => complete),
      ),
      this.onAddressElementChange$.pipe(
        ignoreNullish(),
        map(({ complete }) => complete),
      ),
    ]).pipe(
      map(([isPaymentElementComplete, isAddressElementComplete]) => isPaymentElementComplete && isAddressElementComplete),
    );
  }
  //#endregion

  //#region payments

  setSelectedBusinessEntityId(businessEntityId: number): Observable<void> {
    const existingBusinessEntityId = this.store.selectSnapshot(StripeState.getSelectedBusinessEntityId);
    if (businessEntityId === existingBusinessEntityId) {
      return of(undefined);
    }

    return this.store.dispatch(new SetSelectedBusinessEntityId(businessEntityId));
  }

  getSelectedBusinessEntityId(): Observable<number> {
    return this.store.select(StripeState.getSelectedBusinessEntityId);
  }

  getAdditionalPaymentData(): Observable<PaymentPlatformPaymentData> {
    return this.getSelectedPaymentMethodId().pipe(
      switchMap(selectedPaymentMethodId => this.store.selectOnce(StripeState.getSuccessfulSetupIntentId(selectedPaymentMethodId))),
      map(setupIntentId => ({ setupIntentId })),
    );
  }

  makePayment(chargePayload: ChargePayload): Observable<PaymentResult> {
    return this.paymentsApiService.makePaymentV5(chargePayload);
  }

  cancelPayment(cancelPaymentPayload: CancelPaymentPayload): Observable<unknown> {
    return this.paymentsApiService.cancelPaymentV5(cancelPaymentPayload);
  }

  handlePaymentActionRequirement(
    paymentActionRequirement: PaymentActionRequirement,
    paymentMethodType: PaymentMethodType,
  ): Observable<Error | undefined> {
    if (paymentActionRequirement?.clientSecret == undefined || paymentMethodType == undefined) {
      this.logger.warn('No client secret or payment method type provided. Skipping payment action requirement handling.');
      return of(undefined);
    }

    return getLoadedResults(this.instance$).pipe(
      switchMap(stripeInstance => {
        switch (paymentMethodType) {
          case PaymentMethodType.CARD:
            return stripeInstance.confirmCardPayment(paymentActionRequirement.clientSecret);
          case PaymentMethodType.ACH:
          case PaymentMethodType.US_BANK_ACCOUNT:
            return stripeInstance.confirmUsBankAccountPayment(paymentActionRequirement.clientSecret);
          case PaymentMethodType.ACSS_DEBIT:
            return stripeInstance.confirmAcssDebitPayment(paymentActionRequirement.clientSecret);
        }
      }),
      take(1),
      map(({ paymentIntent, error }) => {
        /**
         * There are 3 possible outcomes (so far, only determined by 3DS authentication)
         *  1. Is cancelled => is treated as a generic authentication failure
         *  2. Fails => `error` contains specific reason (although, could be generic)
         *  3. Succeeds => `paymentIntent.status` is 'succeeded'
         */
        if (error != undefined) {
          return this.marshalError(error);
        }

        // Fallback for anything outside of above 3 outcomes, although this has not been encountered yet. Treat it as a generic failure.
        if (paymentIntent.status !== 'succeeded') {
          return this.marshalError({
            type: 'authentication_error',
            message: 'We are unable to authenticate your payment method. Please choose a different payment method and try again.',
          });
        }

        return undefined; // yay no error!
      }),
    );
  }
  //#endregion

  //#region miscellaneous
  marshalError(error: StripeError): Error {
    if (error == undefined) {
      return undefined;
    }

    return {
      name: error.type,
      message: error.message,
    };
  }

  marshalBillingDetails(billingDetails: PaymentPlatformBillingDetails): BillingDetails {
    if (billingDetails == undefined) {
      return undefined;
    }

    const { postal_code: postalCode, ...address } = billingDetails.address ?? ({} as StripeAddress);

    return {
      ...billingDetails,
      address: { ...address, postalCode },
    };
  }

  unmarshalBillingDetails(billingDetails: BillingDetails): PaymentPlatformBillingDetails {
    if (billingDetails == undefined) {
      return undefined;
    }

    const { postalCode: postal_code, ...address } = billingDetails.address ?? ({} as Address);

    return {
      ...billingDetails,
      address: { ...address, postal_code },
      email: billingDetails.email,
      phone: billingDetails.phone ?? null, // undefined will cause an error in Stripe
    };
  }

  // Stripe rejects confirmSetup when the element's email field is 'never' but no email is supplied.
  // That field is 'never' for every method except one collecting the email in-element (a mandate method
  // with no email already provided). So pass undefined only in that collect-in-element case (the element
  // supplies the value); otherwise send the resolved email, falling back to '' to satisfy the contract.
  private _withConfirmationEmail(billingDetails: BillingDetails, newPaymentMethodType: PaymentMethodType): BillingDetails {
    const requiresBillingEmail = PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED.includes(newPaymentMethodType);
    const email = shouldCollectEmailInElement(requiresBillingEmail, billingDetails.email) ? undefined : (billingDetails.email || '');
    return { ...billingDetails, email };
  }

  private _getBankBillingDetails(newPaymentMethodType: PaymentMethodType): BillingDetails {
    const isWallet = isAWalletPaymentMethodType(newPaymentMethodType);
    const country = this._getCountry(newPaymentMethodType);
    const requiresBillingEmail = PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED.includes(newPaymentMethodType);
    // Direct-debit mandates need a billing email. Prefer one the buyer supplied outside the element,
    // then their account email; leave it undefined (never empty string) so a hidden field uses the
    // provided value and a blank never overwrites the element-collected one.
    const email = requiresBillingEmail
      ? (this.store.selectSnapshot(StripeState.getProvidedBillingEmail) || this.authStateAdaptor.getUserEmailSnapshot() || undefined)
      : (this.authStateAdaptor.getUserEmailSnapshot() || undefined);
    const address =  {
      city: '',
        country,
        line1: '',
        line2: '',
        postalCode: '',
        state: '',
    };
    return {
      name: '',
      email,
      address: isWallet ? undefined : address
    };
  }

  private _getCountry(newPaymentMethodType: PaymentMethodType): string {
    const isAcss = areStringsEqualIgnoreCase(newPaymentMethodType, PaymentMethodType.ACSS_DEBIT);
    const isBacs = [PaymentMethodType.BACS, PaymentMethodType.BACS_DEBIT].some(type =>
      areStringsEqualIgnoreCase(newPaymentMethodType, type)
    );
    if (isAcss) {
      return  CountryAlpha2Code.CANADA;
    }
    if (isBacs) {
      return  CountryAlpha2Code.UNITED_KINGDOM;
    }
    return CountryAlpha2Code.UNITED_STATES;
  }

//#endregion
}
